import { Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import Layout from '@/components/Layout';
import PrivateRoute from '@/components/PrivateRoute';
import HomePage from '@/pages/HomePage';
import LoansListPage from '@/pages/loans/Loans';
import PricingComparisonPage from '@/pages/PricingComparisonPage';
import ValuationReportsPage from '@/pages/ValuationReportsPage';
import MarketColorPage from '@/pages/marketColor/MarketColorPage';
import BenchmarkRatesPage from '@/pages/BenchmarkRatesPage';
import FXRatesPage from '@/pages/FXRatesPage';
import RPXAdjustmentsPage from '@/pages/RPXAdjustmentsPage';
import LoanAnalyticsPage from '@/pages/LoanAnalyticsPage';
import LoanDetailPage from '@/pages/loanDetails/LoanDetailPage';
import LoginPage from '@/pages/LoginPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import { AuthProvider } from '@/contexts/AuthContext';
import { LoansRefreshProvider } from '@/contexts/LoansRefreshContext';
import { GlobalRefreshProvider } from '@/contexts/GlobalRefreshContext';
import { DataChangeProvider, useDataChange } from '@/contexts/DataChangeContext';
import { DataUpdatedModal } from '@/components/DataUpdatedModal';
import { GlobalDataWatcher } from '@/components/GlobalDataWatcher';
import { Toaster } from 'react-hot-toast';
import LoanFormPage from './pages/loanForm/LoanFormPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import ClientPage from './pages/client/ClientPage';
import AdminPage from './pages/admin/AdminPage';
import ProfilePage from './pages/ProfilePage';

// Component to handle global data change modal
const GlobalDataChangeHandler = () => {
  const { dataChangeInfo, clearDataChange } = useDataChange();

  const handleReload = () => {
    window.location.reload();
  };

  const handleDismiss = () => {
    clearDataChange();
  };

  return (
    <DataUpdatedModal
      isOpen={!!dataChangeInfo}
      pageName={dataChangeInfo?.pageName}
      onReload={handleReload}
      onDismiss={handleDismiss}
    />
  );
};

function App() {
  return (
    <AuthProvider>
      <DataChangeProvider>
        <GlobalDataWatcher />
        <GlobalRefreshProvider>
          <Routes data-test-id="app-routes">
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Private routes */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Navigate to="/loans" replace data-test-id="root-redirect" />
            </PrivateRoute>
          }
        />
        <Route
          path="/loans"
          element={
            <PrivateRoute>
              <LoansRefreshProvider>
                <Layout data-test-id="loans-layout">
                  <LoansListPage />
                </Layout>
              </LoansRefreshProvider>
            </PrivateRoute>
          }
        />
        <Route
          path="/loan-form"
          element={
            <PrivateRoute>
              <Layout data-test-id="loan-form-layout">
                <LoanFormPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/loan-form/edit/:loanId"
          element={
            <PrivateRoute>
              <Layout data-test-id="loan-form-edit-layout">
                <LoanFormPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/loan/:loanId"
          element={
            <PrivateRoute>
              <Layout data-test-id="loan-detail-layout">
                <LoanDetailPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <Layout data-test-id="dashboard-layout">
              <DashboardPage />
            </Layout>
          }
        />
        <Route
          path="/pricing-comparison"
          element={
            <PrivateRoute>
              <Layout data-test-id="pricing-comparison-layout">
                <PricingComparisonPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/valuation-reports"
          element={
            <PrivateRoute>
              <Layout data-test-id="valuation-reports-layout">
                <ValuationReportsPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/credit-spreads"
          element={
            <PrivateRoute>
              <Layout data-test-id="market-color-layout">
                <MarketColorPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/benchmark-rates"
          element={
            <PrivateRoute>
              <Layout data-test-id="benchmark-rates-layout">
                <BenchmarkRatesPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/fx-rates"
          element={
            <PrivateRoute>
              <Layout data-test-id="fx-rates-layout">
                <FXRatesPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/rpx-adjustments"
          element={
            <PrivateRoute>
              <Layout data-test-id="rpx-adjustments-layout">
                <RPXAdjustmentsPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/client"
          element={
            <PrivateRoute>
              <Layout data-test-id="client-layout">
                <ClientPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <PrivateRoute>
              <Layout data-test-id="admin-layout">
                <AdminPage />
              </Layout>
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <Layout data-test-id="profile-layout">
                <ProfilePage />
              </Layout>
            </PrivateRoute>
          }
        />
        {/* Legacy route for backward compatibility */}
        <Route
          path="/valuation-config"
          element={
            <Navigate
              to="/launch-config"
              replace
              data-test-id="valuation-config-redirect"
            />
          }
        />
        <Route
          path="/loan-analytics"
          element={
            <PrivateRoute>
              <Layout data-test-id="loan-analytics-layout">
                <LoanAnalyticsPage />
              </Layout>
            </PrivateRoute>
          }
        />
        {/* Optionally keep HomePage route if needed */}
        <Route
          path="/home"
          element={
            <PrivateRoute>
              <Layout data-test-id="home-layout">
                <HomePage />
              </Layout>
            </PrivateRoute>
          }
        />
        </Routes>
          <GlobalDataChangeHandler />
        </GlobalRefreshProvider>
      </DataChangeProvider>
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#4ade80',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
        data-test-id="toaster"
      />
    </AuthProvider>
  );
}

export default App;
